import * as z from 'zod'

const stripePaymentWhenSchema = z.enum([
  'deferred_payment',
  'pitchup_only',
  'immediate_payment',
])

export const chargeTypesInputSchema = z.object({
  campsiteId: z.string().optional(),
  campsiteSlug: z.string(),
  arrive: z.string().nullable(),
  depart: z.string().nullable(),
  adults: z.number().nullable(),
  children: z.number().nullable(),
  childAges: z.string().nullable(),
  nights: z.number().nullable(),
  availability: z.boolean().default(true),
})

const moneySchema = z.object({
  amount: z.number(),
  amountMinor: z.number(),
  currency: z.string(),
})

const chargeTypeStateSchema = z.enum([
  'adults_only',
  'available',
  'disabled',
  'max_capacity',
  'min_nights',
  'unavailable',
])

export const chargeTypeTaxSchema = z.object({
  type: z.string(),
  inclusive: z.boolean(),
  rate: z.number(),
  amount: moneySchema,
})

export const chargeTypeArrivalTaxSchema = z.object({
  name: z.string(),
  amount: moneySchema,
})

export const chargeTypeExtraSchema = z.object({
  name: z.string(),
  price: moneySchema,
  id: z.string(),
  description: z.string(),
  descriptionNeedsTranslation: z.boolean(),
  compulsory: z.boolean(),
  max: z.number().nullable(),
  terms: z.string().array().nullable().optional(),
  isVariable: z.boolean(),
  pricingPeriod: z.number(),
  pricingType: z.object({
    slug: z.string(),
    name: z.string(),
  }),
  pricingDisplay: z.string(),
})

const offerAppliedSchema = z.object({
  name: z.string(),
  discountPercent: z.number(),
})

const quoteSchema = z.object({
  price: moneySchema,
  basePrice: moneySchema,
  preDiscountPrice: moneySchema,
  preDiscountBasePrice: moneySchema,
  preDiscountPriceAdults: moneySchema,
  preDiscountPriceChildren: moneySchema,
  preDiscountPriceInfants: moneySchema,
  adults: z.number(),
  children: z.number(),
  infants: z.number(),
  offersActive: z.boolean(),
  offersApplied: offerAppliedSchema.array(),
})

const ecommerceItemSchema = z.object({
  item_id: z.string(),
  item_name: z.string(),
  item_category: z.string(),
  item_category2: z.string().optional(),
  item_category3: z.string().optional(),
  item_category4: z.string().optional(),
  quantity: z.coerce
    .number()
    .refine((n) => Number.isFinite(n) && n >= 0, 'Invalid quantity'),
  price: z.coerce
    .number()
    .refine((n) => Number.isFinite(n) && n >= 0, 'Invalid price'),
})

const ecommerceSchema = z.object({
  items: z.array(ecommerceItemSchema).catch([]),
})

export const chargeTypeSchema = z.object({
  chargetypeId: z.string(),
  pitchtypeId: z.string(),
  price: moneySchema.nullable(),
  priceExArrivalTaxes: moneySchema.nullable().optional(),
  isAvailable: z.boolean(),
  isAgeRestricted: z.boolean().optional(),
  isOvercapacity: z.boolean(),
  isOvercapacityMessage: z.string().optional().nullable(),
  dogsExceed: z.boolean().nullable().optional(),
  maxDogs: z.number().nullable().optional(),
  isNotifiable: z.boolean(),
  state: chargeTypeStateSchema,
  /**
   * Persons included in the charge type pricing.
   * This is the actual persons included value used for pricing calculations and display.
   * Can differ from `pitchtype.personsIncluded` when the charge type overrides it.
   * Backend provides this value for every charge type result once deployed.
   * Optional and nullable for backward compatibility with backends that don't yet return this field.
   */
  personsIncluded: z.number().optional().nullable(),
  appliedExtras: z
    .array(
      z.object({
        name: z.string(),
        price: z.object({
          amount: z.number(),
          amount_minor: z.number(),
          currency: z.string(),
        }),
        quantity: z.number(),
      }),
    )
    .nullable(),
  availAfterMinStay: z.boolean(),
  minDays: z.number().nullable(),
  pitchesToSell: z.number(),
  stripePaymentWhen: stripePaymentWhenSchema,
  deposit: moneySchema.nullable(),
  remainder: moneySchema.nullable(),
  balanceDueDate: z.string().optional(),
  refundUntilDate: z.string().optional(),
  taxes: chargeTypeTaxSchema.array(),
  arrivalTaxes: chargeTypeArrivalTaxSchema.array(),
  quote: quoteSchema.nullable(),
  extras: chargeTypeExtraSchema.array().nullable(),
  input: chargeTypesInputSchema,
  ecommerce: ecommerceSchema.optional().catch({ items: [] }),
})
